import { useCallback, useEffect, useRef, useState } from "react";
import {
  INTERVALO_ATUALIZACAO_NOTIFICACOES_MS,
  LIMITE_SEM_LOTE,
  aplicarLeitura,
  contarNaoLidas,
  dividirEmLotes,
  extrairMensagemErroApi,
  mapearNotificacoes,
  resumirResultadoEmLote,
} from "../utils/notificacoes";

// ==============================||  NOTIFICACOES ||============================== //

const MSG_SESSAO_EXPIRADA = "Sua sessão expirou. Faça login novamente.";
const MSG_ERRO_CARGA = "Não foi possível carregar as notificações.";
const MSG_ERRO_MARCAR = "Não foi possível marcar a notificação como lida.";

// Disparado no `window` depois de uma marcacao bem-sucedida. O hook escuta e
// recarrega, entao o badge do sino acompanha o que acontece em qualquer outro
// lugar da tela (D-N7).
export const EVENTO_NOTIFICACOES_ALTERADAS = "egrc:notificacoes-alteradas";

export function useGetNotificacoes({
  intervaloMs = INTERVALO_ATUALIZACAO_NOTIFICACOES_MS,
} = {}) {
  const [notificacoes, setNotificacoes] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [sessaoExpirada, setSessaoExpirada] = useState(false);

  // Numero da ultima busca disparada: so a resposta da busca mais recente vale.
  const ultimaBuscaRef = useRef(0);

  // Intervalo invalido (0, negativo, NaN) viraria um laco de requests.
  const intervaloEfetivo =
    Number.isFinite(intervaloMs) && intervaloMs > 0
      ? intervaloMs
      : INTERVALO_ATUALIZACAO_NOTIFICACOES_MS;

  const carregar = useCallback(async () => {
    ultimaBuscaRef.current += 1;
    const busca = ultimaBuscaRef.current;
    const vigente = () => busca === ultimaBuscaRef.current;

    try {
      const token = localStorage.getItem("access_token");

      const response = await fetch(
        `${process.env.REACT_APP_API_URL}notifications`,
        { headers: { Authorization: `Bearer ${token}` } },
      );

      if (!vigente()) return;

      if (response.status === 401) {
        setSessaoExpirada(true);
        setError(MSG_SESSAO_EXPIRADA);
        return;
      }

      if (!response.ok) throw new Error(MSG_ERRO_CARGA);

      const json = await response.json();

      if (!vigente()) return;

      setNotificacoes(mapearNotificacoes(json));
      setSessaoExpirada(false);
      setError(null);
    } catch (err) {
      if (!vigente()) return;

      // Mantem a ultima lista boa: so a mensagem muda.
      setError(MSG_ERRO_CARGA);
    } finally {
      // Carregando e so a primeira carga; as demais sao silenciosas.
      if (vigente()) setIsLoading(false);
    }
  }, []);

  // Busca ao montar.
  useEffect(() => {
    carregar();
  }, [carregar]);

  // Evento global e volta da aba ao primeiro plano recarregam na hora. Com a
  // sessao expirada nenhum dos dois e escutado: um token vencido geraria um 401
  // a cada gatilho (o app usa fetch cru, sem o interceptor de utils/axios.js).
  useEffect(() => {
    if (sessaoExpirada) return undefined;

    const aoAlterar = () => carregar();
    const aoMudarVisibilidade = () => {
      if (document.visibilityState === "visible") carregar();
    };

    window.addEventListener(EVENTO_NOTIFICACOES_ALTERADAS, aoAlterar);
    document.addEventListener("visibilitychange", aoMudarVisibilidade);

    return () => {
      window.removeEventListener(EVENTO_NOTIFICACOES_ALTERADAS, aoAlterar);
      document.removeEventListener("visibilitychange", aoMudarVisibilidade);
    };
  }, [carregar, sessaoExpirada]);

  // Polling: o contrato nao tem push (D-N2). Pausa com a aba oculta (ao voltar,
  // o ouvinte acima recarrega) e para de vez na sessao expirada.
  useEffect(() => {
    if (sessaoExpirada) return undefined;

    const intervalo = setInterval(() => {
      if (document.visibilityState === "visible") carregar();
    }, intervaloEfetivo);

    return () => clearInterval(intervalo);
  }, [carregar, intervaloEfetivo, sessaoExpirada]);

  // Base do otimismo (marcar antes do PUT) e do rollback (desfazer se falhar).
  const definirLeituraLocal = useCallback((id, lida) => {
    setNotificacoes((atual) => aplicarLeitura(atual, id, lida));
  }, []);

  return {
    notificacoes,
    naoLidas: contarNaoLidas(notificacoes),
    isLoading,
    error,
    sessaoExpirada,
    recarregar: carregar,
    definirLeituraLocal,
  };
}

// Corpo de erro ja parseado, ou null. O corpo pode vir vazio (401, 5xx) ou nao
// ser JSON, entao le como texto e so entao tenta o parse.
const lerCorpoDeErro = async (response) => {
  try {
    const texto = await response.text();
    return texto ? JSON.parse(texto) : null;
  } catch (err) {
    return null;
  }
};

const avisarAlteracao = () =>
  window.dispatchEvent(new CustomEvent(EVENTO_NOTIFICACOES_ALTERADAS));

// PUT notifications, com { idNotification, isRead } no BODY (a rota e a raiz).
// O PUT so marca como lida: `isRead: false` devolve 204 e nao faz nada, entao
// esta funcao nunca envia `false` e nao existe "marcar como nao lida". O efeito
// e imediato e sem cache, o que torna seguro o otimismo na tela.
//
// Sucesso e qualquer 2xx (o observado e 204 sem corpo): o corpo nao e lido, nem
// com .json(), que quebraria num corpo vazio. Falha e qualquer nao-2xx ou rede
// fora do ar, e lanca Error com a mensagem para a tela. Id inexistente e 400
// (nao 404), no envelope { <recurso>: [ { code, message } ] }. ProblemDetails
// (texto tecnico em ingles) cai na mensagem padrao; ver extrairMensagemErroApi.
//
// `notificar: false` e para quem marca varias de uma vez (marcarTodasComoLidas)
// e avisa uma unica vez no fim.
export async function marcarNotificacao(
  idNotification,
  { notificar = true } = {},
) {
  let response;

  try {
    const token = localStorage.getItem("access_token");

    response = await fetch(`${process.env.REACT_APP_API_URL}notifications`, {
      method: "PUT",
      headers: {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ idNotification, isRead: true }),
    });
  } catch (err) {
    throw new Error(MSG_ERRO_MARCAR);
  }

  if (response.status === 401) throw new Error(MSG_SESSAO_EXPIRADA);

  if (!response.ok) {
    throw new Error(
      extrairMensagemErroApi(await lerCorpoDeErro(response), MSG_ERRO_MARCAR),
    );
  }

  if (notificar) avisarAlteracao();
}

// Nao ha endpoint em lote: um PUT por notificacao (D-N4). Ate LIMITE_SEM_LOTE
// vao todos juntos; acima disso, em lotes sequenciais (no maximo
// TAMANHO_LOTE_MARCACAO em voo). Cada lote so comeca quando o anterior
// terminou por inteiro. Falha de uma nao interrompe as outras: o resultado vem
// resumido (total, ok, falhas e a mensagem, nula se tudo deu certo). O efeito
// no servidor e permanente, ja que o PUT nao desmarca.
export async function marcarTodasComoLidas(ids) {
  const lista = Array.isArray(ids) ? ids : [];
  const lotes =
    lista.length > LIMITE_SEM_LOTE ? dividirEmLotes(lista) : [lista];
  const resultados = [];

  for (const lote of lotes) {
    // Em sequencia de proposito: o await dentro do laco segura o proximo lote.
    resultados.push(
      ...(await Promise.allSettled(
        lote.map((id) => marcarNotificacao(id, { notificar: false })),
      )),
    );
  }

  const resumo = resumirResultadoEmLote(resultados);

  if (resumo.ok > 0) avisarAlteracao();

  return resumo;
}
